import type { RoomMode } from '@xox/shared'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { saveHumanFirst } from '../components/BossStartPicker'
import type { DuelGameDef } from '../components/duel/defs'
import type { SnapshotOf } from '../store/duel'

interface CreateOptions<S> {
  /** Defaults to the settings last used on this device. */
  settings?: S
  /** cpu rooms: the human opens. Remembered on this device. */
  humanFirst?: boolean
}

/** Creates a room of one duel (two-player or against its bot) and navigates into it. */
export function useCreateRoom<Snap extends SnapshotOf<S>, S extends { turnSeconds: number }>(
  def: DuelGameDef<Snap, S>,
) {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = async (mode: RoomMode = 'pvp', opts: CreateOptions<S> = {}) => {
    const settings = opts.settings ?? def.settings.load()
    const humanFirst = opts.humanFirst ?? false
    setCreating(true)
    setError(null)
    try {
      def.settings.save(settings)
      if (mode === 'cpu') saveHumanFirst(def.id, humanFirst)
      const { code } = await def.api.createRoom(settings, mode, { humanFirst })
      navigate(`${def.roomPath}/${code}`)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setCreating(false)
    }
  }

  return { create, creating, error }
}

/** Creates a party room (no seats to pick) and navigates into it; the error is kept for the panel. */
export function useCreateAndEnter<A extends unknown[]>(
  createRoom: (...args: A) => Promise<{ code: string }>,
  roomPath: string,
) {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = async (...args: A) => {
    setCreating(true)
    setError(null)
    try {
      const { code } = await createRoom(...args)
      navigate(`${roomPath}/${code}`)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setCreating(false)
    }
  }

  return { create, creating, error }
}
